import { Directive, inject, Injector, OnChanges, OnDestroy, runInInjectionContext, SimpleChanges } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MonoTypeOperatorFunction, Observable, of, Subject, SubjectLike, Subscription, SubscriptionLike } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { isNotNullish } from './app/utils/is-not-nullish';

const ngOnChangesSymbol = Symbol('ngOnChanges');
const ngOnDestroySymbol = Symbol('ngOnDestroy');

@Directive()
export abstract class LifecycleComponent implements OnChanges, OnDestroy {
  protected readonly ngOnChanges$: Observable<SimpleChanges>;
  protected readonly ngOnDestroy$: Observable<void>;

  private [ngOnChangesSymbol] = new Subject<SimpleChanges>();
  private [ngOnDestroySymbol] = new Subject<void>();

  protected readonly _injector: Injector;
  protected _routerOutlet?: RouterOutlet | null;
  private readonly _subscriptions = new Subscription();

  constructor() {
    this._injector = inject(Injector);
    this.ngOnChanges$ = this[ngOnChangesSymbol].asObservable();
    this.ngOnDestroy$ = this[ngOnDestroySymbol].asObservable();

    this.ngOnDestroy$.pipe(take(1)).subscribe(() => {
      this[ngOnChangesSymbol].complete();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this[ngOnChangesSymbol].next(changes);
  }
  ngOnDestroy(): void {
    this[ngOnDestroySymbol].next();
    this[ngOnDestroySymbol].complete();
    this._subscriptions.unsubscribe();
  }

  set subscription(value: SubscriptionLike) {
    this._subscriptions.add(value);
  }

  protected takeUntilDestroy<T>(): MonoTypeOperatorFunction<T> {
    return takeUntil(this[ngOnDestroySymbol]);
  }

  protected completeOnDestroy<T extends SubjectLike<any>>(value: T): T {
    this._subscriptions.add(() => value.complete());
    return value;
  }

  protected input$<T extends keyof this>(key: T): Observable<this[T]> {
    return this.ngOnChanges$.pipe(
      map((changes) => (key in changes ? changes[key as keyof typeof changes] : undefined)),
      filter(isNotNullish),
      map((change) => change.currentValue),
      startWith(this[key])
    );
  }

  protected isFromRoute$(): Observable<boolean> {
    this._routerOutlet = this._routerOutlet ?? runInInjectionContext(this._injector, () => inject(RouterOutlet, { optional: true }));

    return (
      this._routerOutlet?.activateEvents.pipe(
        map((instance) => instance === this),
        startWith(this._routerOutlet.isActivated && this._routerOutlet.component === this),
        distinctUntilChanged()
      ) ?? of(false)
    );
  }
}
